import React, { useMemo, useRef, useState, useEffect } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Mousewheel, EffectFade } from "swiper/modules";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import renderOptions from "../utils/textRenderOption";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/keyboard";
import "swiper/css/effect-fade";

import "./Slider.scss";
import { getLargeImageUrl } from "../utils/imageLoader";
import { useLanguage } from "../contexts/LanguageContext";

const Slider = ({
  photos = [],
  activeInfoIds = [],
  setActiveInfoIds,
  setShowInfo,
}) => {
  const swiperRef = useRef(null);
  const [isDimmed, setIsDimmed] = useState(false);
  const [fadeDurationSec, setFadeDurationSec] = useState(2);
  const [isLoaded, setIsLoaded] = useState(false);
  const inactivityTimerRef = useRef(null);
  const dimTimerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const loadedCountRef = useRef(0);
  const { language } = useLanguage();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isInfoPanelOpen =
    activeInfoIds.includes("6UiV9bwDFtnknAYi9Jz14k") ||
    activeInfoIds.includes("Gu72yAGRWo92oXIhDxBmC");

  const brightnessFactor = useMemo(() => {
    const count = activeInfoIds.length;
    if (count === 0) return 1;
    if (isMobile) return 2.25;
    if (count === 1) return 1.5;
    if (count === 2) return 2;
    return 2.25;
  }, [activeInfoIds, isMobile]);

  const handleImageLoad = (e) => {
    const img = e.target;
    // Double-RAF ensures the browser paints at least one frame at opacity:0
    // before we trigger the transition — fixes cached images showing instantly.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        img.style.opacity = "1";
        loadedCountRef.current += 1;
        if (loadedCountRef.current >= 1) {
          setIsLoaded(true);
        }
      });
    });
  };

  const handleActivity = React.useCallback(() => {
    // Clear any existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (dimTimerRef.current) {
      clearTimeout(dimTimerRef.current);
    }

    // If info panels are open, don't trigger the inactivity timer
    if (activeInfoIds.length > 0) {
      setFadeDurationSec(1);
      setIsDimmed(false);
      return;
    }

    // Reset brightness with fade-out duration (1s)
    setFadeDurationSec(1);
    setIsDimmed(false);

    // Start 5s inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      // After 5s, trigger dimming with fade-in duration (5s)
      dimTimerRef.current = setTimeout(() => {
        setFadeDurationSec(8); // take 10 seconds to fade
        setIsDimmed(true);
      }, 0);
    }, 5000); //after 6 seconds inactivity
  }, [activeInfoIds]);

  useEffect(() => {
    // Add multiple event listeners for any interaction
    const events = [
      "mousemove",
      "click",
      "keydown",
      "keyup",
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
    ];
    events.forEach((event) =>
      window.addEventListener(event, handleActivity, { capture: true }),
    );

    // Start initial timer
    handleActivity();

    // Cleanup
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity, { capture: true }),
      );
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (dimTimerRef.current) clearTimeout(dimTimerRef.current);
    };
  }, [handleActivity, activeInfoIds]);

  useEffect(() => {
    // console.log("onResize removing setViewportWidth");
  }, []);

  const items = useMemo(() => {
    // Group assets into slides: a slide is either a "pair" (gauche/droite) or a "single" (full width)
    const slides = [];
    photos.forEach((entry) => {
      const fields = entry?.fields || {};

      // 1. Direct asset link (treated as single)
      if (fields?.file?.url) {
        slides.push({ type: "single", asset: entry, entry });
        return;
      }

      // Check for the new format: 'photos' array containing 2 assets
      const multiPhotos = fields.photos;
      if (Array.isArray(multiPhotos) && multiPhotos.length >= 2) {
        if (isMobile) {
          slides.push({
            type: "single",
            asset: multiPhotos[0],
            entry,
            splitFromPair: "left",
          });
          slides.push({
            type: "single",
            asset: multiPhotos[1],
            entry,
            splitFromPair: "right",
          });
        } else {
          slides.push({
            type: "pair",
            assetGauche: multiPhotos[0],
            assetDroite: multiPhotos[1],
            entry,
          });
        }
        return;
      }

      // 3. Fallback to older formats
      const fg = fields.photoGauche;
      const fd = fields.photoDroite;
      const single = fields.photo;

      // Entry containing photoGauche and/or photoDroite (treated as pair)
      if (fg?.fields?.file?.url || fd?.fields?.file?.url) {
        if (isMobile) {
          if (fg?.fields?.file?.url)
            slides.push({
              type: "single",
              asset: fg,
              entry,
              splitFromPair: "left",
            });
          if (fd?.fields?.file?.url)
            slides.push({
              type: "single",
              asset: fd,
              entry,
              splitFromPair: "right",
            });
        } else {
          slides.push({
            type: "pair",
            assetGauche: fg,
            assetDroite: fd,
            entry,
          });
        }
      }
      // Entry containing a single 'photo' field (treated as single)
      else if (single?.fields?.file?.url) {
        slides.push({ type: "single", asset: single, entry });
      }
    });
    return slides;
  }, [photos, isMobile]);

  const captions = useMemo(() => {
    return items.map((slide) => {
      const fields = slide.entry?.fields || {};

      const getFallback = (asset) => ({
        nodeType: "document",
        data: {},
        content: [
          {
            nodeType: "paragraph",
            data: {},
            content: [
              {
                nodeType: "text",
                value:
                  (asset?.fields?.title || asset?.fields?.titre || "") +
                  (asset?.fields?.description
                    ? `\n${asset.fields.description}`
                    : ""),
                marks: [],
                data: {},
              },
            ],
          },
        ],
      });

      if (slide.type === "single") {
        let doc;
        if (slide.splitFromPair === "left") {
          doc =
            language === "fr"
              ? fields?.descriptionPhotoGaucheFr
              : fields?.descriptionPhotoGaucheEn;
        } else if (slide.splitFromPair === "right") {
          doc =
            language === "fr"
              ? fields?.descriptionPhotoDroiteFr
              : fields?.descriptionPhotoDroiteEn;
        } else {
          doc =
            language === "fr" ? fields?.descriptionFr : fields?.descriptionEn;
        }
        return { single: doc || getFallback(slide.asset) };
      } else {
        const leftDoc =
          language === "fr"
            ? fields?.descriptionPhotoGaucheFr
            : fields?.descriptionPhotoGaucheEn;
        const rightDoc =
          language === "fr"
            ? fields?.descriptionPhotoDroiteFr
            : fields?.descriptionPhotoDroiteEn;
        return {
          left:
            leftDoc ||
            (slide.assetGauche ? getFallback(slide.assetGauche) : null),
          right:
            rightDoc ||
            (slide.assetDroite ? getFallback(slide.assetDroite) : null),
        };
      }
    });
  }, [items, language]);

  // setActiveIndex(0) was causing the caption to reset to the first
  // slide on language toggle because `items` identity changes when Contentful
  // refetches the translated project but `Swiper` keeps its internal index.
  // We rely on `onSwiper` to initialize it to 0 when the gallery actually changes.

  const dimBrightness = items[activeIndex]?.entry?.fields?.brightness ?? 2.5;

  return (
    <div
      className={`swiperContainer ${isDimmed ? "dimmed" : ""} ${isLoaded ? "loaded" : "loading"}`}
      style={{
        "--fade-duration": `${fadeDurationSec}s`,
        "--brightness-factor": brightnessFactor,
        "--dim-brightness": dimBrightness,
      }}
      onClick={(e) => {
        // Close active info blocks if they are open
        if (activeInfoIds.length > 0) {
          setActiveInfoIds([]);
          if (setShowInfo) setShowInfo(false);
          return;
        }

        if (setShowInfo) setShowInfo(false);
        swiperRef.current?.slideNext();
        handleActivity();
      }}
    >
      <Swiper
        key={`${items.length}-${photos[0]?.sys?.id || "initial"}`}
        modules={[Navigation, Keyboard, Mousewheel, EffectFade]}
        navigation
        keyboard={{ enabled: true }}
        loop={items.length > 1}
        slidesPerView={1}
        slidesPerGroup={1}
        spaceBetween={0}
        speed={500}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        mousewheel={true}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setActiveIndex(swiper.realIndex);
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          handleActivity();
        }}
        className={`mainSwiper ${isDimmed ? "is-dimmed" : ""}`}
      >
        {items.map((slide, idx) => (
          <SwiperSlide key={idx}>
            {slide.type === "pair" ? (
              <div className="pairContainer">
                <div className="halfSlide leftHalf">
                  {slide.assetGauche && (
                    <img
                      src={getLargeImageUrl(slide.assetGauche)}
                      alt="Laure Phillipe"
                      width={
                        slide.assetGauche.fields?.file?.details?.image?.width
                      }
                      height={
                        slide.assetGauche.fields?.file?.details?.image?.height
                      }
                      style={{ opacity: 0, transition: "opacity 0.4s ease" }}
                      onLoad={handleImageLoad}
                    />
                  )}
                </div>
                <div className="halfSlide rightHalf">
                  {slide.assetDroite && (
                    <img
                      src={getLargeImageUrl(slide.assetDroite)}
                      alt="Laure Phillipe"
                      width={
                        slide.assetDroite.fields?.file?.details?.image?.width
                      }
                      height={
                        slide.assetDroite.fields?.file?.details?.image?.height
                      }
                      style={{ opacity: 0, transition: "opacity 0.4s ease" }}
                      onLoad={handleImageLoad}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="singleContainer">
                <img
                  src={getLargeImageUrl(slide.asset)}
                  alt="Laure Phillipe"
                  width={slide.asset.fields?.file?.details?.image?.width}
                  height={slide.asset.fields?.file?.details?.image?.height}
                  style={{ opacity: 0, transition: "opacity 0.4s ease" }}
                  onLoad={handleImageLoad}
                />
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {!isInfoPanelOpen && items[activeIndex]?.type === "pair" && (
        <>
          <div className="fixedCaption leftCaption">
            {captions[activeIndex]?.left &&
              documentToReactComponents(
                captions[activeIndex].left,
                renderOptions,
              )}
          </div>
          <div className="fixedCaption rightCaption">
            {captions[activeIndex]?.right &&
              documentToReactComponents(
                captions[activeIndex].right,
                renderOptions,
              )}
          </div>
        </>
      )}
      {!isInfoPanelOpen && items[activeIndex]?.type === "single" && (
        <div className="fixedCaption singleCaption">
          {captions[activeIndex]?.single &&
            documentToReactComponents(
              captions[activeIndex].single,
              renderOptions,
            )}
        </div>
      )}

      {activeInfoIds.length > 0 && (
        <div
          className="slider-overlay"
          onClick={() => setActiveInfoIds([])}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10,
            cursor: "default",
          }}
        />
      )}
    </div>
  );
};

export default Slider;
